import dayjs from 'dayjs'

import { getI18n } from '@/plugins/i18n'
import { formatDayMonthDate, formatShortDate } from '@/lib/utils'
import { useCurrency } from '@/composables/currency'
import { dateRangePresetDefinitions } from '@/composables/date-range-presets'
import { useAccountingStore } from '@/stores/accounting.store'
import { dateRangePresetEnum } from '@/stores/portal.store'

export const formatDuration = (minutes, signed = false) => {
  if (minutes == null || minutes === '') return ''
  const value = Number(minutes)
  const sign = signed && value !== 0 ? (value > 0 ? '+' : '−') : ''
  const absolute = Math.abs(Math.round(value))
  return `${sign}${Math.floor(absolute / 60)}:${String(absolute % 60).padStart(2, '0')}`
}

export const parseDuration = (text) => {
  if (text == null || text === '') return null
  if (typeof text === 'number') return Math.round(text)
  const value = String(text).trim()
  if (value.includes(':')) {
    const [hours, minutes] = value.split(':')
    return Number(hours) * 60 + Number(minutes || 0)
  }
  return Math.round(Number(value.replace(',', '.')) * 60)
}

export const formatTimeValue = (value) => (value ? String(value).slice(0, 5) : '')

export const reportPeriodLabel = (report) => {
  const i18n = getI18n()
  const preset = dateRangePresetDefinitions.find(p => p.value === report.period_preset)
  const range = `${formatShortDate(report.date_from)} – ${formatShortDate(report.date_to)}`
  if (!preset) return range
  return `${preset.labelCount != null ? i18n.t(preset.labelKey, preset.labelCount) : i18n.t(preset.labelKey)} · ${range}`
}

export const reportPeriodShortLabel = (report) => {
  const from = dayjs(report.date_from)
  if (report.period_preset === dateRangePresetEnum.MONTH) return from.format('MMM YYYY')
  if (report.period_preset === dateRangePresetEnum.QUARTER) return `Q${Math.floor(from.month() / 3) + 1} ${from.year()}`
  if (report.period_preset === dateRangePresetEnum.YEAR) return String(from.year())
  return `${formatDayMonthDate(report.date_from)} – ${formatShortDate(report.date_to)}`
}

export const nextReportPeriodStart = (report) => dayjs(report.date_to).add(1, 'day')

export const reportRowTitle = (row) => [
  row.date || row.document_date ? formatShortDate(row.date || row.document_date) : '',
  row.project || row.name || row.number,
].filter(Boolean).join(' · ')

const numberFormatters = new Map()
const numberFormatter = (locale) => {
  if (!numberFormatters.has(locale)) numberFormatters.set(locale, new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }))
  return numberFormatters.get(locale)
}

export const useReportFormatter = () => {
  const { getCurrencyString } = useCurrency()
  const { reportColumnKindEnum, reportDurationFormatEnum } = useAccountingStore()
  const SERVER_TEXT_KINDS = new Set([reportColumnKindEnum.TIME_NOTE, reportColumnKindEnum.EXCHANGE_RATE])

  const number = (value) => (value == null || value === '' ? '' : numberFormatter(getI18n().locale.value).format(Number(value)))
  const money = (value, currency) => (value == null || value === '' || !currency ? '' : getCurrencyString(Number(value), currency))
  const duration = (minutes, build, signed = false) => {
    if (build?.config?.duration_format !== reportDurationFormatEnum.DECIMAL) return formatDuration(minutes, signed)
    if (minutes == null || minutes === '') return ''
    return `${signed && minutes > 0 ? '+' : ''}${number(minutes / 60)}`
  }
  const time = (value, row, isEnd = false) => {
    if (!value) return ''
    return `${formatTimeValue(value)}${isEnd && row.crosses_midnight ? ' (+1)' : ''}${row.assumed ? '*' : ''}`
  }

  const cell = (column, row, build) => {
    const value = row[column.key]
    if (value === undefined || SERVER_TEXT_KINDS.has(column.kind)) return ''
    switch (column.kind) {
      case reportColumnKindEnum.TIME: return time(value, row, column.is_end)
      case reportColumnKindEnum.MONEY_BY_CURRENCY: return Object.entries(value || {}).map(([currency, amount]) => money(amount, currency)).join(' · ')
      case reportColumnKindEnum.PERCENT: return value == null ? '' : `${number(value * 100)}%`
      case reportColumnKindEnum.DATE: return value ? formatShortDate(value) : ''
      case reportColumnKindEnum.DURATION: return duration(value, build)
      case reportColumnKindEnum.SIGNED_DURATION: return duration(value, build, true)
      case reportColumnKindEnum.MONEY: return money(value, row.currency || build.reference_currency)
      case reportColumnKindEnum.REFERENCE_MONEY: return money(value, build.reference_currency)
      case reportColumnKindEnum.RATE: return value == null ? '' : `${money(value, build.reference_currency)}/h`
      case reportColumnKindEnum.NUMBER: return number(value)
      default: return value == null ? '' : String(value)
    }
  }

  const totalCell = (column, totals, build, currency = null) => {
    const value = totals?.[column.key]
    if (value == null) return ''
    switch (column.kind) {
      case reportColumnKindEnum.DURATION: return duration(value, build)
      case reportColumnKindEnum.SIGNED_DURATION: return duration(value, build, true)
      case reportColumnKindEnum.MONEY: return money(value, currency || build.reference_currency)
      case reportColumnKindEnum.REFERENCE_MONEY: return money(value, currency || build.reference_currency)
      default: return number(value)
    }
  }

  return { number, money, duration, time, cell, totalCell }
}

export const reportFormattedRowKindEnum = Object.freeze({
  GROUP: 0,
  DATA: 1,
  SUBTOTAL: 2,
  TOTAL: 3,
  CURRENCY_TOTAL: 4,
})

export const formattedSectionRows = (formattedSection, totalLabel) => {
  const toRow = (kind, cells, label = null) => Object.fromEntries([
    ['kind', kind],
    ...cells.map((cell, index) => [`c${index}`, index === 0 && !cell && label ? label : cell]),
  ])
  const rows = []
  formattedSection.groups.forEach((group) => {
    if (group.label) rows.push(toRow(reportFormattedRowKindEnum.GROUP, [group.label]))
    group.rows.forEach(cells => rows.push(toRow(reportFormattedRowKindEnum.DATA, cells)))
    if (group.label && group.totals) rows.push(toRow(reportFormattedRowKindEnum.SUBTOTAL, group.totals, group.label))
  })
  if (formattedSection.totals) rows.push(toRow(reportFormattedRowKindEnum.TOTAL, formattedSection.totals, totalLabel))
  formattedSection.currency_totals.forEach(item => rows.push(toRow(reportFormattedRowKindEnum.CURRENCY_TOTAL, item.cells, item.label)))
  return rows.map((row, index) => ({ ...row, id: index }))
}

export const useReportSubject = () => {
  const accountingStore = useAccountingStore()
  const subjectLabel = (report) => (accountingStore.isStaffSubject(report.subject) ? report.staff_label : report.company_name || report.listing_name)
  const subjectIcon = (report) => accountingStore.getLocalizedReportSubject(report.subject)?.icon
  const subjectChips = (subject) => {
    const definition = accountingStore.getLocalizedReportSubject(subject)
    return definition ? [{ id: definition.id, name: definition.name, color: definition.paletteColor }] : []
  }
  return { subjectLabel, subjectIcon, subjectChips }
}

const TIME_FIELDS = new Set(['start', 'end'])
const TEXT_FIELDS = new Set(['comments', 'notes', 'client_reference'])

const sameReportValue = (field, a, b) => {
  if (TIME_FIELDS.has(field)) return formatTimeValue(a) === formatTimeValue(b)
  if (a == null || a === '' || b == null || b === '') return (a ?? '') === (b ?? '')
  return typeof a === 'boolean' || TEXT_FIELDS.has(field) ? a === b : Number(a) === Number(b)
}

export const isReportFieldModified = (row, field) => !!row.original && field in row.original && !sameReportValue(field, row[field], row.original[field])

export const isReportRowModified = (row) => !!row.original && Object.keys(row.original).some(field => field !== 'is_excluded' && isReportFieldModified(row, field))
